import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, } from 'react-router-dom';
import MyReviews from "./components/MyReviews";
import MyImages from "./components/MyImages";
import { businessImageUpload } from "../../../services/businessService"; 
import MyExperience from "./components/MyExperience";
import MyServices from "./components/MyServices";
import { formatPhone } from "../../../utils/formatPhone";
import { editUserProfile } from "../../../services/userService";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { getImage } from "../../../utils/cloudinarySizing";

const BusinessProfileOwnerView = ({
    data,
    isFavorited,
    favoriteCount,
    handleFavoriteToggle,
    id,
    setModalOpen,
    handleUpdate
}) => {
    // Overlay state for enlarged profile photo
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [showContactInfo, setShowContactInfo] = useState(false);
    const [savingContact, setSavingContact] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const contactRef = useRef(null);

    const [activeComponent, setActiveComponent] = useState('Services');

    const [sliderIndex, setSliderIndex] = useState(0);

    const navigate = useNavigate();

    const location = useLocation();

    const { setProfileThumbnail } = useAuth();

    const [contactForm, setContactForm] = useState({
        contact_email: data?.profile?.contact_email || "",
        phone_number: data?.profile?.phone_number || "",
    });

    const tabs = [
        { name: "Services", icon: "fa-solid fa-wrench" },
        { name: "Experience", icon: "fa-solid fa-hourglass-half" },
        { name: "Images", icon: "fa-solid fa-image" },
        { name: "Reviews", icon: "fa-solid fa-star", style: { color: "#ffcc00" } },
    ];

    const handleTabClick = (tab, index) => {
        setActiveComponent(tab);
        setSliderIndex(index);
        navigate(`/business/${id}/${tab.toLowerCase()}`);
    };

    const handleOverlayToggle = () => {
        setOverlayOpen(!isOverlayOpen);
    };

    const handleToggleContactInfo = () => {
        setShowContactInfo(prev => !prev);
    };

    const handleEmailChange = (e) => {
        setContactForm(prev => ({
            ...prev,
            contact_email: e.target.value,
        }));
    };

    const handlePhoneChange = (e) => {
        setContactForm(prev => ({
            ...prev,
            phone_number: e.target.value,
        }));
    };

    const handleSaveContactInfo = async () => {
        try {
            setSavingContact(true);

            const formData = new FormData();
            formData.append("contact_email", contactForm.contact_email);
            formData.append("phone_number", contactForm.phone_number);

            await editUserProfile(formData);

            toast.success("Contact info updated successfully");

            setShowContactInfo(false);
        } catch (err) {
            console.error(err);
        } finally {
            setSavingContact(false);
        }
    };

    const handleCancelContactEdit = () => {
        setShowContactInfo(false);
    };

    const renderComponent = () => {
        switch (activeComponent) {
            case "Services":
                return <MyServices />;
            case 'Experience':
                return <MyExperience data={data} />;
            case "Images":
                return <MyImages id={data.id} />
            case 'Reviews':
                return <MyReviews businessId={data.id} />;
            default:
                return null;
        }
    };

    const renderTabButtons = () => (
        tabs.map((tab, index) => (
            <button
                key={tab.name}
                className={`nav-button ${activeComponent === tab.name ? "active" : ""}`}
                onClick={() => handleTabClick(tab.name, index)}
            >
                {tab.icon && <i className={tab.icon} style={tab.style}></i>}
                <span style={{ marginLeft: "8px" }}>
                    {/* Removed conditional check for "Media" to simplify rendering */}
                    {tab.name}
                </span>
            </button>
        ))
    );

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            alert("No file selected");
            return;
        }

        const formData = new FormData();
        formData.append("photo_original", file);

        try {
            const response = await businessImageUpload(formData);

            if (response.status === 200) {
                const updated = response.data;

                // 🔥 1. Update header thumbnail + persistence
                if (updated.photo_original) {
                    setProfileThumbnail(updated.photo_original);
                    localStorage.setItem("avatar", updated.photo_original);
                }

                // 🔥 2. Update business profile UI
                handleUpdate({
                    ...data,
                    profile: {
                        ...data.profile,
                        photo_original: updated.photo_original,
                    }
                });

                alert("Image uploaded successfully");
            } else {
                alert("Failed to upload image");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Error uploading image");
        }
    };

    const formatLocation = (city, state) => {
        if (!city && !state) return null;
        if (city && state) return ` ${city}, ${state}`;
        return city || state;
    };

    useEffect(() => {
        const current = location.pathname.split("/").pop();
        const match = tabs.findIndex(
            tab => tab.name.toLowerCase() === current.toLowerCase()
        );

        if (match !== -1) {
            setActiveComponent(tabs[match].name);
            setSliderIndex(match);
        } else {
            setActiveComponent("Services");
            setSliderIndex(0);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        handleResize(); // initialize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        setContactForm({
            contact_email: data?.profile?.contact_email || "",
            phone_number: data?.profile?.phone_number || "",
        });
    }, [data]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contactRef.current && !contactRef.current.contains(e.target)) {
                setShowContactInfo(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);


    return (
        <div className="business-page-wrapper">
            <div className="business-profile-container">
                <div className="centered-content">
                    <div className="business-profile-content">
                        <div className="photo-and-reviews">
                            {/* Profile Photo */}
                            <div className="business-profile-photo" >
                                <img onClick={handleOverlayToggle} src={getImage(data?.profile?.photo_original, "avatar_sm") || '/servspace-business-placeholder-200.png'} alt="Profile" />

                                <div className="edit-business-profile-photo" onClick={() => document.getElementById('file-input').click()}>
                                    <input type="file"
                                        onChange={handleUpload}
                                        style={{ display: 'none' }} id="file-input" />
                                    <label className="camera-icon">
                                        <i className="fa-solid fa-camera"></i>
                                    </label>
                                </div>
                            </div>

                            {/* Overlay for Enlarged Image */}
                            {isOverlayOpen && (
                                <div className="image-overlay" onClick={handleOverlayToggle}>
                                    <img src={data?.profile?.photo_original || '/servspace-business-placeholder.png'} alt="Enlarged Profile" />
                                </div>
                            )}


                            <div className="business-name-icons-wrapper">
                                <div className="business-name-row">
                                    <h3 className="business-name">
                                        {data?.business_name || ""}
                                    </h3>
                                    <span className="business-handle">@{data?.username}</span>
                                </div>

                                <div className="business-icons">

                                    {/* Contact button */}
                                    <div className="contact-button-container" ref={contactRef}>
                                        <button
                                            className="contact-button"
                                            onClick={handleToggleContactInfo}
                                        >
                                            Contact Info
                                            <i className={`fa-solid fa-chevron-${showContactInfo ? "up" : "down"}`} />
                                        </button>

                                        {showContactInfo && (
                                            <div className="contact-dropdown">
                                                <input
                                                    type="email"
                                                    placeholder="Contact email"
                                                    value={contactForm.contact_email}
                                                    onChange={handleEmailChange}
                                                />

                                                <input
                                                    type="text"
                                                    placeholder="Phone number"
                                                    value={formatPhone(contactForm.phone_number)}
                                                    onChange={handlePhoneChange}
                                                />

                                                <div className="contact-actions">
                                                    <button
                                                        className="contact-save-btn"
                                                        onClick={handleSaveContactInfo}
                                                        disabled={savingContact}
                                                    >
                                                        {savingContact ? "Saving..." : "Save"}
                                                    </button>

                                                    <button
                                                        className="contact-cancel-btn"
                                                        onClick={handleCancelContactEdit}
                                                        disabled={savingContact}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Location group */}
                                    {(data?.profile?.city || data?.profile?.state) && (
                                        <div className="freelancer-location">
                                            <i className="fa-solid fa-location-dot"></i>
                                            <span>
                                                {formatLocation(data.profile.city, data.profile.state)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Divider — only show if location AND at least one link exists */}
                                    {(data?.profile?.city || data?.profile?.state) &&
                                        (data.portfolio_link ||
                                            data.facebook ||
                                            data.instagram ||
                                            data.x ||
                                            data.youtube) && (
                                            <span className="freelancer-divider">|</span>
                                        )}

                                    <div className="own-business-links-row">
                                        {/* Website */}
                                        {data?.website && (
                                            <a
                                                href={data.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="business-portfolio-icon"
                                                title="Website"
                                            >
                                                <i className="fa-solid fa-link"></i>
                                            </a>
                                        )}

                                        {/* Facebook */}
                                        {data?.facebook && (
                                            <a
                                                href={data.facebook}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="business-portfolio-icon"
                                                title="Facebook"
                                            >
                                                <i className="fa-brands fa-facebook"></i>
                                            </a>
                                        )}

                                        {/* Instagram */}
                                        {data?.instagram && (
                                            <a
                                                href={data.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="business-portfolio-icon"
                                                title="Instagram"
                                            >
                                                <i className="fa-brands fa-instagram"></i>
                                            </a>
                                        )}

                                        {/* X / Twitter */}
                                        {data?.x && (
                                            <a
                                                href={data.x}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="business-portfolio-icon"
                                                title="X"
                                            >
                                                <i className="fa-brands fa-x-twitter"></i>
                                            </a>
                                        )}

                                        {/* YouTube */}
                                        {data?.youtube && (
                                            <a
                                                href={data.youtube}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="business-portfolio-icon"
                                                title="YouTube"
                                            >
                                                <i className="fa-brands fa-youtube"></i>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isMobile ? (
                            <>
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p>
                                            <i className="fa-solid fa-star"></i> {data?.average_rating}
                                        </p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>

                                <div className="freelancer-buttons">
                                    <button
                                        className="edit-button"
                                        onClick={() => setModalOpen(true)}
                                    >
                                        <i className="fa-solid fa-pen"></i>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="profile-stats-buttons">
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p>
                                            <i className="fa-solid fa-star"></i> {data?.average_rating}
                                        </p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>

                                <div className="freelancer-buttons">
                                    <button
                                        className="mobile-edit-button"
                                        onClick={() => setModalOpen(true)}
                                    >
                                        <i className="fa-solid fa-pen"></i> Edit
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>

                    <div className="container">
                        <div className="button-row">
                            {/* Sliding Bar */}
                            <div
                                className="freelancer-slider"
                                style={{
                                    width: `calc(100% / ${tabs.length})`,
                                    transform: `translateX(${sliderIndex * 100}%)`,
                                }}
                            />
                            {renderTabButtons()}
                        </div>
                        {renderComponent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessProfileOwnerView;
